import React from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  View,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useMultiPayContractsQuery } from 'apollo/queries/autogenerate/hooks';
import {
  MultipayContract,
  MultipayContractStatus,
} from 'apollo/queries/autogenerate/schemas';
import Icon from 'shared/modules/common/components/atoms/Icon';
import Loading from 'shared/modules/common/components/atoms/Loading';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import NavigationBar from 'web/modules/common/components/molecules/NavigationBar';
import routes from 'web/navigation/routes';

export default function MultiPayContractsScreen() {
  const navigate = useNavigate();
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  // Fetch only active, approved, pending MultipayContracts
  const {
    data: contracts,
    loading: isLoading,
    error: error,
  } = useMultiPayContractsQuery({
    variables: {
      status: [
        MultipayContractStatus.ACTIVE,
        MultipayContractStatus.APPROVED,
        MultipayContractStatus.PENDING,
      ],
    },
  });

  const multiPayContracts = contracts?.multipayContracts || null;

  if (isLoading && !contracts) {
    return <Loading id="loading-indicator" />;
  }

  if (multiPayContracts === null || error) {
    return <Empty text="エラーが発生しました" />;
  }

  const handlePressContractItem = (selected: MultipayContract) => {
    navigate(
      routes.authenticated.usage.multiPayContractDetail.replace(
        ':id',
        selected.id
      )
    );
  };

  return (
    <View style={[styles.screen, isBigScreen && styles.screenBigScreen]}>
      <View
        style={[styles.container, isBigScreen && styles.containerBigScreen]}
      >
        <NavigationBar
          title="分割払い一覧"
          onBackButtonPress={() => navigate(-1)}
          id="navigation-bar"
        />
        <FlatList
          data={multiPayContracts}
          renderItem={(item) => {
            return (
              <Item
                contract={item.item}
                isTop={item.index === 0}
                isBottom={item.index === multiPayContracts.length - 1}
                onPress={handlePressContractItem}
                id={`multipay-contracts-item-${item.index}`}
              />
            );
          }}
          ItemSeparatorComponent={() => (
            <View style={styles.separatorContainer}>
              <View style={styles.separator} />
            </View>
          )}
          ListEmptyComponent={<Empty text={`分割払い契約はありません`} />}
        />
      </View>
    </View>
  );
}

const Item = ({
  contract,
  isTop,
  isBottom,
  onPress,
  id,
}: {
  contract: MultipayContract;
  isTop: boolean;
  isBottom: boolean;
  onPress: (selected: MultipayContract) => void;
  id?: string;
}) => {
  const topRadius = isTop ? 8 : 0;
  const bottomRadius = isBottom ? 8 : 0;

  return (
    <TouchableOpacity
      onPress={() => onPress(contract)}
      /** Generate HTML and Test IDs  */
      {...generateID(id)}
      /** Platform-specific props */
      {...Platform.select({
        web: {
          accessibilityRole: 'button',
        },
      })}
    >
      <View
        style={[
          styles.itemContainer,
          {
            borderTopStartRadius: topRadius,
            borderTopEndRadius: topRadius,
            borderBottomStartRadius: bottomRadius,
            borderBottomEndRadius: bottomRadius,
          },
        ]}
      >
        <Text style={styles.itemDate}>
          {contract.approvalDecisionAt
            ? `契約日 ${formatYearMonthDate(contract.approvalDecisionAt, 'en')}`
            : '審査中'}
        </Text>
        <Text style={styles.itemAmount}>
          {formatToCurrency(contract.principal + contract.contractFee)}
        </Text>
        <Icon size="12" name="caret-right" tintColor={colors.greyLight} />
      </View>
    </TouchableOpacity>
  );
};

const Empty = ({ text }: { text: string }) => {
  return (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>{text}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    alignItems: 'center',
    padding: 16,
  },
  screenBigScreen: {
    padding: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 960,
  },
  itemContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    padding: 16,
  },
  itemDate: {
    ...typography.caption01,
    fontSize: 13,
    fontWeight: '700',
    color: colors.greyDark,
  },
  itemAmount: {
    ...typography.title03,
    flex: 1,
    marginHorizontal: 16,
    fontSize: 16,
    fontWeight: '700',
    color: colors.greyDark,
    textAlign: 'right',
  },
  separatorContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  separator: {
    flex: 1,
    height: 1,
    marginHorizontal: 16,
    backgroundColor: colors.greySuperLight,
  },
  emptyContainer: {
    alignContent: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    borderRadius: 8,
  },
  emptyText: {
    ...typography.caption02,
    fontSize: 13,
    color: colors.greyMedium,
    textAlign: 'center',
    marginVertical: 22,
  },
});
